/**
 * ataService.ts
 * Proxy toutes les requêtes Google RAG (FileSearch) vers le backend Python.
 * La clé GOOGLE_API_KEY n'est JAMAIS exposée côté client.
 * Toutes les requêtes sont authentifiées via le token Azure AD Bearer.
 */
import { RagStore, Document, QueryResult, CustomMetadata } from "../pages/ata/types";

export type AtaQueryMode = "question" | "agent";
export type AtaModel = "gemini-3-flash-preview" | "gemini-3.1-pro-preview";
export const DEFAULT_ATA_MODEL: AtaModel = "gemini-3-flash-preview";

export interface UploadJobProgress {
    status: string;
    stage?: string;
    progressPct?: number;
    pagesDone?: number;
    totalPages?: number | null;
    filename?: string;
}

const CHUNK_DISPLAY_RE = /^(?<base>.+)__p\d{4}-\d{4}\.txt$/i;

function getLogicalDisplayName(displayName: string): string {
    const match = CHUNK_DISPLAY_RE.exec(displayName);
    return match?.groups?.base ?? displayName;
}

function normalizeDocuments(documents: Document[]): Document[] {
    const grouped = new Map<string, Document[]>();

    for (const doc of documents) {
        const key = getLogicalDisplayName(doc.displayName || doc.name).toLowerCase();
        const arr = grouped.get(key) ?? [];
        arr.push(doc);
        grouped.set(key, arr);
    }

    const normalized: Document[] = [];
    for (const groupDocs of grouped.values()) {
        if (groupDocs.length === 1) {
            normalized.push({
                ...groupDocs[0],
                relatedDocumentNames: [groupDocs[0].name]
            });
            continue;
        }

        const first = groupDocs[0];
        const displayName = getLogicalDisplayName(first.displayName || first.name);
        const hasFailed = groupDocs.some(d => (d.state || "").toUpperCase().includes("FAILED"));
        const hasProcessing = groupDocs.some(d => (d.state || "").toUpperCase().includes("PROCESSING"));
        const note = groupDocs.find(d => (d.note || "").trim().length > 0)?.note;

        normalized.push({
            ...first,
            displayName,
            state: hasFailed ? "FAILED" : hasProcessing ? "PROCESSING" : first.state,
            sizeBytes: groupDocs.reduce((sum, d) => sum + (d.sizeBytes || 0), 0),
            note,
            relatedDocumentNames: groupDocs.map(d => d.name)
        });
    }

    return normalized.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "", "fr", { sensitivity: "base" }));
}

export async function updateDocumentNote(
    storeName: string,
    documentDisplayName: string,
    note: string,
    token?: string
): Promise<{ updated: boolean; note: string; documentDisplayName: string }> {
    const response = await fetch("/api/ata/stores/notes", {
        method: "PUT",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ storeName, documentDisplayName, note })
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || response.statusText);
    }
    return response.json();
}

function getAuthHeaders(token?: string): Record<string, string> {
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listRagStores(token?: string, includeDocs = false): Promise<RagStore[]> {
    const url = includeDocs ? "/api/ata/stores?includeDocs=true" : "/api/ata/stores";
    const response = await fetch(url, {
        headers: getAuthHeaders(token)
    });
    if (!response.ok) throw new Error(`Impossible de lister les catégories : ${response.statusText}`);
    const stores = (await response.json()) as RagStore[];
    if (!includeDocs) return stores;
    return stores.map(store => ({
        ...store,
        documents: store.documents ? normalizeDocuments(store.documents) : store.documents
    }));
}

export async function createRagStore(displayName: string, token?: string): Promise<RagStore> {
    const response = await fetch("/api/ata/stores", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ displayName })
    });
    if (!response.ok) throw new Error(`Impossible de créer la catégorie : ${response.statusText}`);
    const data = await response.json();
    return { name: data.name, displayName: data.displayName || displayName };
}

export async function deleteRagStore(ragStoreName: string, token?: string): Promise<void> {
    const response = await fetch(`/api/ata/stores?storeName=${encodeURIComponent(ragStoreName)}`, {
        method: "DELETE",
        headers: getAuthHeaders(token)
    });
    if (!response.ok) throw new Error(`Impossible de supprimer la catégorie : ${response.statusText}`);
}

export async function listDocuments(ragStoreName: string, token?: string, storeDisplayName?: string): Promise<Document[]> {
    let url = `/api/ata/stores/files?storeName=${encodeURIComponent(ragStoreName)}`;
    if (storeDisplayName) url += `&storeDisplayName=${encodeURIComponent(storeDisplayName)}`;
    const response = await fetch(url, { headers: getAuthHeaders(token) });
    if (!response.ok) throw new Error(`Impossible de lister les documents : ${response.statusText}`);
    const docs = (await response.json()) as Document[];
    return normalizeDocuments(docs);
}

export async function uploadToRagStore(
    storeDisplayName: string,
    file: File,
    token?: string,
    onJobStarted?: () => void,
    onProgress?: (progress: UploadJobProgress) => void,
    _filename?: string // internal — used by _pollUploadJob
): Promise<{ document: Document; storeName: string; storeDisplayName: string }> {
    const filename = _filename ?? file.name;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storeDisplayName", storeDisplayName);
    const response = await fetch("/api/ata/stores/upload", {
        method: "POST",
        headers: getAuthHeaders(token),
        body: formData
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || response.statusText);
    }
    const data = await response.json();

    // Backend returns 202 + jobId for OCR-heavy files — poll until done
    if (response.status === 202 && data.jobId) {
        // File is safely on the server — notify caller so it can inform the user
        onJobStarted?.();
        return _pollUploadJob(data.jobId, filename, token, onProgress);
    }

    return {
        storeName: data.storeName ?? "",
        storeDisplayName: data.storeDisplayName ?? storeDisplayName,
        document: {
            ...(data.document ?? {}),
            displayName: data.document?.displayName || filename
        }
    };
}

async function _pollUploadJob(
    jobId: string,
    filename: string,
    token?: string,
    onProgress?: (progress: UploadJobProgress) => void
): Promise<{ document: Document; storeName: string; storeDisplayName: string }> {
    // Poll every 5 s for up to 60 minutes (720 attempts)
    // Large PDFs are split into 50-page chunks processed sequentially by Gemini,
    // which can easily exceed 10 minutes for documents with hundreds of pages.
    let unknownJobCount = 0;
    for (let i = 0; i < 720; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const res = await fetch(`/api/ata/upload-status/${jobId}`, {
            headers: getAuthHeaders(token)
        });
        if (!res.ok) {
            if (res.status === 404) {
                const err = await res.json().catch(() => ({}) as any);
                if (err?.error === "unknown job") {
                    unknownJobCount += 1;
                    // Allow transient worker-switch races before failing hard.
                    if (unknownJobCount <= 12) {
                        continue;
                    }
                    throw new Error(
                        "Suivi de l'upload indisponible (job introuvable). Le traitement a peut-être changé de worker serveur ; veuillez réessayer."
                    );
                }
            }
            throw new Error(`Erreur lors du suivi de l'upload : ${res.statusText}`);
        }

        unknownJobCount = 0;
        const data = await res.json();
        if (data.status === "processing") {
            onProgress?.({
                status: data.status,
                stage: data.stage,
                progressPct: typeof data.progressPct === "number" ? data.progressPct : undefined,
                pagesDone: typeof data.pagesDone === "number" ? data.pagesDone : undefined,
                totalPages: typeof data.totalPages === "number" ? data.totalPages : (data.totalPages ?? null),
                filename: data.filename || filename
            });
        }
        if (data.status === "done") {
            onProgress?.({
                status: data.status,
                stage: data.stage,
                progressPct: 100,
                pagesDone: typeof data.pagesDone === "number" ? data.pagesDone : undefined,
                totalPages: typeof data.totalPages === "number" ? data.totalPages : (data.totalPages ?? null),
                filename: data.filename || filename
            });
            return {
                storeName: data.storeName ?? "",
                storeDisplayName: data.storeDisplayName ?? "",
                document: {
                    ...(data.document ?? {}),
                    displayName: data.document?.displayName || filename
                }
            };
        }
        if (data.status === "error") {
            onProgress?.({
                status: data.status,
                stage: data.stage,
                progressPct: typeof data.progressPct === "number" ? data.progressPct : undefined,
                pagesDone: typeof data.pagesDone === "number" ? data.pagesDone : undefined,
                totalPages: typeof data.totalPages === "number" ? data.totalPages : (data.totalPages ?? null),
                filename: data.filename || filename
            });
            throw new Error(data.error || "Erreur lors du traitement du document");
        }
        // status === "processing" → continue polling
    }
    throw new Error("Timeout : le traitement OCR a pris trop de temps (> 60 min)");
}

export async function deleteDocument(_ragStoreName: string, documentName: string, token?: string, blobPath?: string): Promise<void> {
    let url = `/api/ata/files?fileName=${encodeURIComponent(documentName)}`;
    if (blobPath) url += `&blobPath=${encodeURIComponent(blobPath)}`;
    const response = await fetch(url, { method: "DELETE", headers: getAuthHeaders(token) });
    if (!response.ok) throw new Error(`Impossible de supprimer le document : ${response.statusText}`);
}

export async function moveDocument(
    payload: {
        sourceStoreName: string;
        sourceStoreDisplayName: string;
        targetStoreDisplayName: string;
        document: Document;
    },
    token?: string
): Promise<{ moved: boolean; targetStoreName: string; targetStoreDisplayName: string }> {
    const response = await fetch("/api/ata/files/move", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({
            sourceStoreName: payload.sourceStoreName,
            sourceStoreDisplayName: payload.sourceStoreDisplayName,
            targetStoreDisplayName: payload.targetStoreDisplayName,
            documentName: payload.document.name,
            documentDisplayName: payload.document.displayName,
            relatedDocumentNames: payload.document.relatedDocumentNames ?? [payload.document.name],
            blobPath: payload.document.blobPath,
            note: payload.document.note ?? ""
        })
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || response.statusText);
    }
    return response.json();
}

export async function fileSearch(
    ragStoreNames: string[],
    fileNames: string[],
    query: string,
    token?: string,
    history?: Array<{ role: "user" | "model"; text: string }>,
    mode: AtaQueryMode = "agent",
    model: AtaModel = DEFAULT_ATA_MODEL
): Promise<QueryResult> {
    const response = await fetch("/api/ata/query", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ storeNames: ragStoreNames, fileNames, query, history: history ?? [], mode, model })
    });
    if (!response.ok) throw new Error(`Erreur de recherche RAG : ${response.statusText}`);
    return response.json();
}

export async function getQueryPlanSteps(
    query: string,
    token?: string,
    history?: Array<{ role: "user" | "model"; text: string }>,
    model: AtaModel = DEFAULT_ATA_MODEL
): Promise<string[]> {
    const response = await fetch("/api/ata/query-plan", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ query, history: history ?? [], model })
    });
    if (!response.ok) return [];
    const data = await response.json().catch(() => ({}));
    const steps = Array.isArray(data?.steps) ? data.steps : [];
    return steps.map((s: unknown) => String(s || "").trim()).filter(Boolean);
}

export interface QueryJobStartResponse {
    jobId: string;
    status: string;
}

export interface QueryJobStatus {
    status: "processing" | "done" | "error";
    stage?: string;
    stageLabel?: string;
    progressPct?: number;
    steps?: string[];
    stepStates?: QueryJobStepState[];
    result?: QueryResult;
    error?: string;
}

export interface QueryJobStepState {
    name: string;
    status: "pending" | "in_progress" | "done" | "error";
    result?: string;
}

export async function startFileSearchQueryJob(
    ragStoreNames: string[],
    fileNames: string[],
    query: string,
    token?: string,
    history?: Array<{ role: "user" | "model"; text: string }>,
    mode: AtaQueryMode = "agent",
    model: AtaModel = DEFAULT_ATA_MODEL
): Promise<QueryJobStartResponse> {
    const response = await fetch("/api/ata/query/start", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ storeNames: ragStoreNames, fileNames, query, history: history ?? [], mode, model })
    });
    if (!response.ok) throw new Error(`Erreur de démarrage de recherche RAG : ${response.statusText}`);
    return response.json();
}

export async function getFileSearchQueryJobStatus(jobId: string, token?: string): Promise<QueryJobStatus> {
    const response = await fetch(`/api/ata/query-status/${encodeURIComponent(jobId)}`, {
        headers: getAuthHeaders(token)
    });
    if (!response.ok) throw new Error(`Erreur de suivi de recherche RAG : ${response.statusText}`);
    return response.json();
}

export async function generateExampleQuestions(ragStoreName: string, token?: string): Promise<string[]> {
    try {
        const response = await fetch("/api/ata/questions", {
            method: "POST",
            headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
            body: JSON.stringify({ storeName: ragStoreName })
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.questions || [];
    } catch {
        return [];
    }
}

// ---------------------------------------------------------------------------
// ATA Conversation History
// ---------------------------------------------------------------------------

export interface ConversationSummary {
    id: number;
    createdAt: string;
    title: string;
    storeNames: string[];
    documentNames: string[];
}

export interface ConversationDetail extends ConversationSummary {
    messages: Array<{ role: string; text: string; groundingChunks?: unknown[] }>;
}

export async function listConversations(token?: string): Promise<ConversationSummary[]> {
    const res = await fetch("/api/ata/history", { headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error(`Impossible de charger l'historique : ${res.statusText}`);
    return res.json();
}

export async function getConversation(id: number, token?: string): Promise<ConversationDetail> {
    const res = await fetch(`/api/ata/history/${id}`, { headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error(`Conversation introuvable : ${res.statusText}`);
    return res.json();
}

export async function saveConversation(
    messages: Array<{ role: string; text: string; groundingChunks?: unknown[] }>,
    storeNames: string[],
    documentNames: string[],
    token?: string,
    existingId?: number
): Promise<{ id: number; createdAt: string }> {
    const res = await fetch("/api/ata/history", {
        method: "POST",
        headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ id: existingId, messages, storeNames, documentNames })
    });
    if (!res.ok) throw new Error(`Impossible de sauvegarder la conversation : ${res.statusText}`);
    return res.json();
}

export async function deleteConversation(id: number, token?: string): Promise<void> {
    const res = await fetch(`/api/ata/history/${id}`, { method: "DELETE", headers: getAuthHeaders(token) });
    if (!res.ok) throw new Error(`Impossible de supprimer la conversation : ${res.statusText}`);
}
